import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BellRing, Loader2, Mail, MessageSquare, Smartphone } from "lucide-react";

const CHANNEL = {
  push: { label: "Push", icon: Smartphone },
  email: { label: "Email", icon: Mail },
  sms: { label: "Text", icon: MessageSquare },
};

const STATUS_TONE = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  skipped: "bg-amber-50 text-amber-800 border-amber-200",
};

const STATUS_WORD = { sent: "Delivered", failed: "Failed", skipped: "Not sent" };

const FILTERS = [
  { id: "all", text: "Everything" },
  { id: "push", text: "Push" },
  { id: "email", text: "Email" },
  { id: "sms", text: "Text" },
  { id: "failed", text: "Problems only" },
];

const Stat = ({ channel, data }) => {
  const Icon = CHANNEL[channel].icon;
  return (
    <div
      className="rounded-lg border border-border bg-white p-4 sm:p-5"
      data-testid={`notifications-stat-${channel}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-gold" />
        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
          {CHANNEL[channel].label}
        </p>
      </div>
      <p className="font-display text-2xl font-bold text-royal-deep mt-2">{data.sent}</p>
      <p className="text-xs text-muted-foreground mt-1">
        delivered
        {data.failed > 0 && <span className="text-red-700 font-semibold"> · {data.failed} failed</span>}
        {data.skipped > 0 && <span className="text-amber-700"> · {data.skipped} not sent</span>}
      </p>
    </div>
  );
};

export default function Notifications() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    const params = { limit: 200 };
    if (filter === "failed") params.status = "failed";
    else if (filter !== "all") params.channel = filter;
    let live = true;
    setRows(null);
    setError("");
    api
      .get("/notifications", { params })
      .then(({ data }) => {
        if (!live) return; // a slower earlier request must never overwrite a newer one
        setRows(data);
      })
      .catch(() => live && setError("We couldn't load the delivery history. Try again in a moment."));
    api.get("/notifications/summary").then(({ data }) => live && setSummary(data)).catch(() => {});
    return () => {
      live = false;
    };
  }, [filter]);

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Delivery</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">
          Notifications
        </h1>
        <p className="text-muted-foreground mt-3 text-sm md:text-base">
          Every push, email and text the platform sent — what reached its device or inbox, what
          didn't, and why.
          {summary?.scope === "mine" && " You're seeing the ones sent to you."}
        </p>
      </div>

      {summary && (
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
          {Object.keys(CHANNEL).map((ch) => (
            <Stat key={ch} channel={ch} data={summary.channels[ch]} />
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="notifications-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            data-testid={`notifications-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors duration-200 ${
              filter === f.id
                ? "border-royal bg-royal text-white"
                : "border-border bg-white text-royal hover:border-royal/40"
            }`}
          >
            {f.text}
          </button>
        ))}
      </div>

      {!rows ? (
        error ? (
          <p className="text-sm text-destructive" data-testid="notifications-error">
            {error}
          </p>
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-royal" data-testid="notifications-loading" />
        )
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-royal/25 bg-white p-10 text-center"
          data-testid="notifications-empty"
        >
          <BellRing className="h-7 w-7 text-gold mx-auto" />
          <p className="font-display text-lg font-semibold text-royal-deep mt-3">
            Nothing sent yet
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Reminders, alerts, owner emails and invoices will appear here the moment they go out.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white divide-y divide-border overflow-hidden">
          {rows.map((r) => {
            const Icon = (CHANNEL[r.channel] || CHANNEL.push).icon;
            return (
              <div key={r.id} data-testid={`notification-row-${r.id}`} className="px-4 sm:px-5 py-3.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Icon className="h-3.5 w-3.5 text-royal shrink-0" />
                  <span
                    data-testid={`notification-status-${r.id}`}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                      STATUS_TONE[r.status] || STATUS_TONE.skipped
                    }`}
                  >
                    {STATUS_WORD[r.status] || r.status}
                  </span>
                  <p className="text-sm font-semibold text-royal-deep truncate max-w-full">
                    {r.title || r.body || "Notification"}
                  </p>
                  <p className="ml-auto text-[11px] text-muted-foreground whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                  </p>
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  To {r.to || "unknown"}
                  {r.kind ? ` · ${r.kind}` : ""}
                </p>
                {r.reason && (
                  <p
                    className="mt-1.5 text-xs text-red-700"
                    data-testid={`notification-reason-${r.id}`}
                  >
                    {r.reason}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
